import { LightMode } from "@/components/ui/color-mode";
import { CenterLock, DEFAULT_ZOOM, MAX_ZOOM } from "@/components/modal/map/map-utils";
import { GOOGLE_API_KEY, GOOGLE_MAP_ID } from "@/constants/endpoints";
import { EstablishmentFieldRequestDetail } from "@/services/establishment-field-request.service";
import { AppColors } from "@/styles/colors";
import { APIProvider, AdvancedMarker, Map, Polygon } from "@vis.gl/react-google-maps";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useMemo } from "react";
import { RequestTypes } from "@/constants/enums";

const REQUEST_MAP_MIN_ZOOM = 5;

const computeCentroid = (points: { latitud: number; longitud: number }[]) => {
  if (!points.length) return { lat: 0, lng: 0 };
  const sum = points.reduce(
    (acc, p) => ({ lat: acc.lat + p.latitud, lng: acc.lng + p.longitud }),
    { lat: 0, lng: 0 },
  );
  return { lat: sum.lat / points.length, lng: sum.lng / points.length };
};

interface Props {
  detail: EstablishmentFieldRequestDetail;
}

export const EstablishmentFieldRequestDetailMap = ({ detail }: Props) => {
  const isLote = detail.idTipoSolicitud === RequestTypes.SolicitudLote;

  const establishmentCenter = useMemo(
    () => ({ lat: detail.latitud ?? 0, lng: detail.longitud ?? 0 }),
    [detail.latitud, detail.longitud],
  );

  const loteCentroid = useMemo(
    () => computeCentroid(detail.coordenadasPoligono?.puntos ?? []),
    [detail.coordenadasPoligono],
  );

  const lotePath = useMemo(
    () =>
      detail.coordenadasPoligono?.puntos?.map((p) => ({
        lat: p.latitud,
        lng: p.longitud,
      })) ?? [],
    [detail.coordenadasPoligono],
  );

  const contextPaths = useMemo(() => {
    const raw = detail.poligonosEstablecimiento as any;
    if (!raw) return [];
    if (Array.isArray(raw)) {
      return raw
        .map(
          (lote: any) =>
            lote.coordenadasPoligono?.puntos?.map((p: any) => ({
              lat: p.latitud,
              lng: p.longitud,
            })) ?? [],
        )
        .filter((path: any[]) => path.length > 0);
    }
    const path = (raw.puntos ?? []).map((p: any) => ({ lat: p.latitud, lng: p.longitud }));
    return path.length > 0 ? [path] : [];
  }, [detail.poligonosEstablecimiento]);

  const hasCoordinates = Boolean(detail.latitud || detail.longitud);
  const mapCenter = isLote ? loteCentroid : establishmentCenter;
  const showMap = isLote ? lotePath.length > 0 : hasCoordinates;

  return (
    <Box
      height={{ base: "280px", md: "360px", "2xl": "460px" }}
      width="100%"
      borderRadius="8px"
      overflow="hidden"
      border={`1px solid ${AppColors.WHITE_BORDER_1}`}
    >
      {showMap ? (
        <LightMode>
          <APIProvider apiKey={GOOGLE_API_KEY}>
            <Map
              defaultCenter={mapCenter}
              defaultZoom={DEFAULT_ZOOM}
              minZoom={REQUEST_MAP_MIN_ZOOM}
              maxZoom={MAX_ZOOM}
              disableDefaultUI
              gestureHandling="greedy"
              style={{ width: "100%", height: "100%" }}
              mapId={GOOGLE_MAP_ID}
            >
              <CenterLock center={mapCenter} />

              {isLote ? (
                <>
                  {contextPaths.map((path, i) => (
                    <Polygon
                      key={i}
                      paths={path}
                      strokeColor={AppColors.LIGHT_GRAY_TEXT}
                      strokeOpacity={0.9}
                      strokeWeight={2}
                      fillColor={AppColors.LIGHT_GRAY_TEXT}
                      fillOpacity={0.25}
                    />
                  ))}
                  <Polygon
                    paths={lotePath}
                    strokeColor={AppColors.ERROR_TEXT}
                    strokeOpacity={0.9}
                    strokeWeight={2}
                    fillColor={AppColors.ERROR_TEXT}
                    fillOpacity={0.2}
                  />
                </>
              ) : (
                <>
                  <AdvancedMarker position={establishmentCenter} />
                  {contextPaths.map((path, i) => (
                    <Polygon
                      key={i}
                      paths={path}
                      strokeColor={AppColors.LIGHT_GRAY_TEXT}
                      strokeOpacity={0.9}
                      strokeWeight={2}
                      fillColor={AppColors.LIGHT_GRAY_TEXT}
                      fillOpacity={0.25}
                    />
                  ))}
                </>
              )}
            </Map>
          </APIProvider>
        </LightMode>
      ) : (
        <Flex
          height="100%"
          width="100%"
          alignItems="center"
          justifyContent="center"
          backgroundColor={AppColors.BACKGROUND_SECONDARY}
        >
          <Text fontSize="14px" color={AppColors.GRAY_TEXT_ALT}>
            Ubicación no disponible
          </Text>
        </Flex>
      )}
    </Box>
  );
};
